import { Controller } from "@hotwired/stimulus"

// Persists an in-progress note (Trix/ActionText) to localStorage so a refresh,
// accidental navigation, or browser crash doesn't lose it. Restores into an
// empty editor on load, saves on edit (debounced), and clears the draft once
// the note saves successfully.
export default class extends Controller {
  static targets = ["restored", "status"]
  static values = {
    key: String,
    debounceMs: { type: Number, default: 500 }
  }

  connect() {
    this.editor = this.element.querySelector("trix-editor")
    if (!this.editor) return

    this.boundSchedule = this.scheduleSave.bind(this)
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this)
    this.editor.addEventListener("trix-change", this.boundSchedule)
    this.element.addEventListener("turbo:submit-end", this.boundSubmitEnd)

    if (this.editor.editor) {
      this.restore()
    } else {
      this.editor.addEventListener("trix-initialize", () => this.restore(), { once: true })
    }
  }

  disconnect() {
    if (this.editor) this.editor.removeEventListener("trix-change", this.boundSchedule)
    this.element.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
    if (this.timer) clearTimeout(this.timer)
  }

  restore() {
    const saved = this.read()
    // Only restore into an empty editor so we never clobber existing content.
    if (!saved || !this.isEmpty()) return

    this.editor.editor.loadHTML(saved)
    if (this.hasRestoredTarget) this.restoredTarget.classList.remove("d-none")
  }

  scheduleSave() {
    if (this.timer) clearTimeout(this.timer)
    if (!this.isEmpty()) this.setStatus("saving")
    this.timer = setTimeout(() => this.save(), this.debounceMsValue)
  }

  save() {
    if (this.isEmpty()) {
      this.clear()
      this.setStatus(null)
    } else {
      this.write(this.editor.value)
      this.setStatus("saved")
    }
  }

  // Clear the draft only on a successful save; a failed submit keeps it.
  handleSubmitEnd(event) {
    if (event.detail && event.detail.success) {
      if (this.hasRestoredTarget) this.restoredTarget.classList.add("d-none")
      this.clear()
      this.setStatus(null)
    }
  }

  setStatus(state) {
    if (!this.hasStatusTarget) return
    const states = {
      saving: ['<i class="fa-regular fa-spinner fa-spin me-1"></i>Saving draft…', "text-muted"],
      saved: ['<i class="fa-regular fa-circle-check me-1"></i>Draft saved', "text-success"]
    }
    const [html, cls] = states[state] || ["", "text-muted"]
    this.statusTarget.innerHTML = html
    this.statusTarget.className = `small ${cls}`
  }

  dismiss() {
    if (this.hasRestoredTarget) this.restoredTarget.classList.add("d-none")
  }

  isEmpty() {
    return this.editor.value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim() === ""
  }

  read() {
    try { return window.localStorage.getItem(this.keyValue) } catch { return null }
  }

  write(value) {
    try { window.localStorage.setItem(this.keyValue, value) } catch { /* quota/private mode */ }
  }

  clear() {
    try { window.localStorage.removeItem(this.keyValue) } catch { /* ignore */ }
  }
}
